import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API, getImageUrl } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { NotificationBell, registerPushSubscription } from '../components/NotificationBell';
import QRScanner from '../components/QRScanner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Activity,
  Users,
  ClipboardCheck,
  Calendar,
  LogOut,
  Home,
  UserPlus,
  QrCode,
  MapPin,
  Clock,
  Building2,
  Plus,
  ChevronRight,
  Loader2,
  Check,
  Camera,
  Pencil,
  X,
  Eye
} from 'lucide-react';
import PlanBanner from '../components/PlanBanner';
import { Copy, RefreshCw, Shield, Code, Terminal, Key, FileText, Download, History, Mail, DollarSign, TrendingUp, Wallet, ArrowRight } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../i18n';
import { Progress } from '../components/ui/progress';
import CatracaAPIPage from './gym/CatracaAPIPage';
import GymFinancial from './gym/GymFinancial';
import GymContactPage from './gym/GymContactPage';

// Dashboard Overview
const GymOverview = () => {
  const [dashboard, setDashboard] = useState(null);
  const [gymPlan, setGymPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, planRes] = await Promise.all([
        axios.get(`${API}/gym/dashboard`),
        axios.get(`${API}/gyms/plan`).catch(() => ({ data: {} }))
      ]);
      setDashboard(dashRes.data);
      setGymPlan(planRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Banner */}
      <PlanBanner planData={gymPlan} variant="gym" />

      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Dashboard da Academia
        </h1>
        <p className="text-slate-600 mt-1">Visão geral dos alunos corporativos</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 text-sm font-medium">Total de Alunos</span>
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-orange-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {dashboard?.total_students || 0}
            </p>
            <p className="text-slate-500 text-sm mt-1">Alunos corporativos ativos</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 text-sm font-medium">Check-ins Hoje</span>
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {dashboard?.today_checkins || 0}
            </p>
            <p className="text-slate-500 text-sm mt-1">Presentes hoje</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 text-sm font-medium">Avaliações Pendentes</span>
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {dashboard?.pending_evaluations || 0}
            </p>
            <p className="text-slate-500 text-sm mt-1">Precisam de avaliação</p>
          </CardContent>
        </Card>
      </div>

      {/* Students by Company */}
      {dashboard?.students_by_company?.length > 0 && (
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Alunos por Empresa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboard.students_by_company.map((company, index) => (
                <div 
                  key={company.company_id || company.name || index}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-medium text-slate-900">{company.name}</span>
                  </div>
                  <span className="text-2xl font-bold text-slate-900">{company.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Students Management
const StudentsManagement = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRegDialog, setShowRegDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [editData, setEditData] = useState({ name: '', email: '', department: '' });
  const [regData, setRegData] = useState({ name: '', email: '', cellphone: '', address: '' });
  const [passCode, setPassCode] = useState('');
  const [validatingPass, setValidatingPass] = useState(false);
  const [passResult, setPassResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/students`);
      setStudents(res.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEdit = (student) => {
    setEditStudent(student);
    setEditData({ name: student.name, email: student.email || '', department: student.department || '' });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API}/students/${editStudent.student_id}`, editData);
      toast.success('Cadastro atualizado!');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestRegister = async (e) => {
    e.preventDefault();
    if (!regData.name.trim()) { toast.error('Nome obrigatório'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/gyms/student-requests`, regData);
      toast.success('Solicitação enviada para aprovação do admin!');
      setShowRegDialog(false);
      setRegData({ name: '', email: '', cellphone: '', address: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidatePass = async () => {
    if (!passCode.trim() || passCode.trim().length !== 3) {
      toast.error('O código deve ter 3 dígitos numéricos');
      return;
    }
    setValidatingPass(true);
    setPassResult(null);
    try {
      const res = await axios.post(`${API}/gym/validate-pass-code`, { code: passCode.trim() });
      setPassResult(res.data);
      toast.success(res.data.message);
      setPassCode('');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Código inválido';
      toast.error(detail);
      setPassResult({ success: false, message: detail });
    } finally {
      setValidatingPass(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Alunos</h1>
          <p className="text-slate-600 mt-1">Alunos vinculados à academia</p>
        </div>
        <Button onClick={() => setShowRegDialog(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="request-register-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Solicitar Cadastro
        </Button>
      </div>

      {/* Pass Code Validation */}
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Validar Vínculo do Aluno</p>
          <div className="flex gap-3">
            <Input
              data-testid="pass-code-input"
              value={passCode}
              onChange={e => setPassCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="Ex: 482"
              className="font-mono text-center text-2xl tracking-[0.4em] max-w-[140px]"
              maxLength={3}
              inputMode="numeric"
              onKeyDown={e => e.key === 'Enter' && handleValidatePass()}
            />
            <Button
              onClick={handleValidatePass}
              disabled={validatingPass || passCode.trim().length !== 3}
              data-testid="validate-pass-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
            >
              {validatingPass ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Validar
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Peça o código de 3 dígitos ao aluno vinculado</p>
          {passResult && (
            <div className={`mt-3 p-4 rounded-xl text-sm ${
              passResult.success === false ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            }`} data-testid="pass-validate-result">
              {passResult.success ? (
                <div className="flex items-center gap-3">
                  {passResult.profile_photo ? (
                    <img src={getImageUrl(passResult.profile_photo)} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-emerald-200" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-emerald-200 flex items-center justify-center text-lg font-bold text-emerald-700">{passResult.student_name?.charAt(0)}</div>
                  )}
                  <div>
                    <p className="font-bold text-base">{passResult.student_name}</p>
                    <p className="text-xs mt-0.5">Empresa: {passResult.company_name || '-'}</p>
                    {passResult.plan && <p className="text-xs">Plano: {passResult.plan.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>}
                    {passResult.bound_modalities?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {passResult.bound_modalities.map(mod => (
                          <span key={mod} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">{mod}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs font-semibold text-emerald-700 mt-1">{passResult.message}</p>
                  </div>
                </div>
              ) : (
                <p className="font-medium">{passResult.message}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {students.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum aluno vinculado</h3>
            <p className="text-slate-500">Solicite o cadastro de novos alunos clicando no botão acima.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-100">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="gym-students-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Aluno</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Empresa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Plano</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Modalidades</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden sm:table-cell">Contato</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden lg:table-cell">Vínculo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student.student_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`student-row-${student.student_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {student.profile_photo ? (
                            <img src={getImageUrl(student.profile_photo)} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">{student.name?.charAt(0)}</div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-900">{student.name}</p>
                            <p className="text-[11px] text-slate-400 hidden sm:block">{student.email || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{student.company_name || '-'}</td>
                      <td className="px-4 py-3">
                        {student.plan ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700" data-testid={`student-plan-${student.student_id}`}>
                            {student.plan.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {student.bound_modalities?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {student.bound_modalities.map(mod => (
                              <span key={mod} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700" data-testid={`student-mod-${student.student_id}-${mod}`}>{mod}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 hidden sm:table-cell">
                        <div>
                          <p className="text-xs">{student.cellphone || student.phone || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500 hidden lg:table-cell">
                        {student.primary_gym_set_at ? (
                          <span className="text-xs">{new Date(student.primary_gym_set_at).toLocaleDateString('pt-BR')}</span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(student)} data-testid={`edit-btn-${student.student_id}`}>
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Student Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-orange-500" /> Editar Aluno</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4 mt-2">
            <div className="space-y-2"><Label>Nome</Label><Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Departamento</Label><Input value={editData.department} onChange={(e) => setEditData({ ...editData, department: e.target.value })} /></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-orange-500 hover:bg-orange-600">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Registration Request Dialog */}
      <Dialog open={showRegDialog} onOpenChange={setShowRegDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-orange-500" /> Solicitar Cadastro de Aluno</DialogTitle>
            <DialogDescription>O cadastro será enviado para aprovação do administrador.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequestRegister} className="space-y-4 mt-2">
            <div className="space-y-2"><Label>Nome *</Label><Input value={regData.name} onChange={(e) => setRegData({ ...regData, name: e.target.value })} required data-testid="reg-name-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={regData.email} onChange={(e) => setRegData({ ...regData, email: e.target.value })} data-testid="reg-email-input" /></div>
            <div className="space-y-2"><Label>Celular</Label><Input value={regData.cellphone} onChange={(e) => setRegData({ ...regData, cellphone: e.target.value })} placeholder="(00) 00000-0000" data-testid="reg-cellphone-input" /></div>
            <div className="space-y-2"><Label>Endereço</Label><Input value={regData.address} onChange={(e) => setRegData({ ...regData, address: e.target.value })} data-testid="reg-address-input" /></div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowRegDialog(false)} className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={submitting} className="flex-1 bg-orange-500 hover:bg-orange-600" data-testid="submit-reg-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar Solicitação'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Check-ins Management
const CheckinsManagement = () => {
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchCheckins = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/checkins/gym?date=${today}`);
      setCheckins(res.data);
    } catch (error) {
      console.error('Error fetching checkins:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCheckins();
  }, [fetchCheckins]);

  const handleValidateCode = async () => {
    if (!code.trim() || code.trim().length !== 5) {
      toast.error('O código deve ter 5 caracteres (3 números + 2 letras)');
      return;
    }
    setValidating(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/gym/validate-checkin-code`, { code: code.trim() });
      setResult(res.data);
      if (res.data.already_checked_in) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      fetchCheckins();
      setCode('');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Código inválido';
      toast.error(detail);
      setResult({ success: false, message: detail });
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/checkins/gym/report-pdf?date=${today}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `checkins_${today}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório PDF baixado!');
    } catch {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Check-ins de Hoje
          </h1>
          <p className="text-slate-600 mt-1">Valide códigos de check-in e acompanhe a frequência</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadPDF}
          disabled={downloading}
          data-testid="download-pdf-btn"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
          Baixar PDF
        </Button>
      </div>

      {/* Code Validation */}
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Validar Código de Check-in Extra</p>
          <div className="flex gap-3">
            <Input
              data-testid="checkin-code-input"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Ex: 482AB"
              className="font-mono text-center text-lg tracking-widest max-w-xs"
              maxLength={5}
              onKeyDown={e => e.key === 'Enter' && handleValidateCode()}
            />
            <Button
              onClick={handleValidateCode}
              disabled={validating || code.trim().length !== 5}
              data-testid="validate-checkin-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6"
            >
              {validating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Validar
            </Button>
          </div>
          {result && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${
              result.success === false ? 'bg-red-50 text-red-700' :
              result.already_checked_in ? 'bg-blue-50 text-blue-700' :
              result.is_extra ? 'bg-amber-50 text-amber-700' :
              'bg-emerald-50 text-emerald-700'
            }`} data-testid="validate-result">
              <div className="flex items-center gap-3">
                {result.student_photo && (
                  <img src={getImageUrl(result.student_photo)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white shadow" />
                )}
                <div>
                  <p className="font-semibold">{result.student_name || 'Erro'}</p>
                  <p>{result.message}</p>
                  {result.modality && <p className="text-xs mt-1 font-medium">Modalidade: {result.modality}</p>}
                  {result.is_extra && <p className="text-xs mt-1">Check-in Extra (academia sem vínculo)</p>}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Checkins */}
      <Card className="border-slate-100">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold flex items-center justify-between" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <span className="flex items-center gap-2"><Calendar className="w-5 h-5 text-orange-500" /> Check-ins do Dia</span>
            <span className="text-sm font-normal text-slate-500">{checkins.length} check-in{checkins.length !== 1 ? 's' : ''}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {checkins.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum check-in hoje</h3>
              <p className="text-slate-500">Os alunos ainda não fizeram check-in hoje.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="today-checkins-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Aluno</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Empresa</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Modalidade</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Horário</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.map((checkin) => (
                    <tr key={checkin.checkin_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                        <div className="flex items-center gap-3">
                          {checkin.student_photo ? (
                            <img src={getImageUrl(checkin.student_photo)} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs font-bold">
                              {(checkin.student_name || 'N')[0]}
                            </div>
                          )}
                          {checkin.student_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {checkin.company_name || 'Sem vínculo'}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {checkin.modality ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700" data-testid={`checkin-modality-${checkin.checkin_id}`}>
                            {checkin.modality}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(checkin.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          checkin.is_extra ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
                        }`}>
                          {checkin.is_extra ? 'Extra' : 'Vinculado'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Check-in History (30 days) with Date Picker
const CheckinHistory = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/checkins/gym/history?days=30`);
      setSummary(res.data);
    } catch (e) { console.error('Failed to load checkin summary:', e); }
  }, []);

  const fetchCheckins = useCallback(async (date) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/checkins/gym?date=${date}`);
      setCheckins(res.data);
    } catch {
      toast.error('Erro ao carregar check-ins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (selectedDate) fetchCheckins(selectedDate);
  }, [selectedDate, fetchCheckins]);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/checkins/gym/report-pdf?date=${selectedDate}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `checkins_${selectedDate}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório PDF baixado!');
    } catch {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="checkin-history">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Histórico de Check-ins
          </h1>
          <p className="text-slate-600 mt-1">Selecione uma data para ver os check-ins</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={downloading} data-testid="download-history-pdf">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
          Baixar PDF
        </Button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">{summary.total_checkins}</p>
              <p className="text-sm text-slate-500">Check-ins (30 dias)</p>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">{summary.total_days}</p>
              <p className="text-sm text-slate-500">Dias com Presença</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Date Picker */}
      <Card className="border-slate-100">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-sm font-medium text-slate-700">Selecionar Data:</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              min={new Date(Date.now() - 30*24*60*60*1000).toISOString().split('T')[0]}
              className="max-w-[200px]"
              data-testid="history-date-picker"
            />
            <span className="text-sm text-slate-500">
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Checkins for selected date */}
      <Card className="border-slate-100">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold flex items-center justify-between" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              Check-ins de {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}
            </span>
            <span className="text-sm font-normal text-slate-500">{checkins.length} check-in{checkins.length !== 1 ? 's' : ''}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : checkins.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nenhum check-in nesta data</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="history-checkins-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Aluno</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Empresa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Modalidade</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Horário</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {checkins.map((c) => (
                    <tr key={c.checkin_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-800">{c.student_name || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{c.company_name || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        {c.modality ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{c.modality}</span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {new Date(c.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.is_extra ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {c.is_extra ? 'Extra' : 'Vinculado'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Gym Profile Page
const GymProfile = () => {
  const [gym, setGym] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({});
  const [photoUrl, setPhotoUrl] = useState(null);

  const MODALITIES = ['Musculação', 'Muay Thai', 'Dança', 'Spinning', 'Pilates', 'Abdômen', 'Treinamento Funcional'];
  const PLANS = [
    { id: 'basic', name: 'Fit' },
    { id: 'plus', name: 'Plus' },
    { id: 'premium', name: 'Premium' },
    { id: 'gold', name: 'Gold' },
    { id: 'platinum', name: 'Platinum' },
    { id: 'action', name: 'Action' },
    { id: 'personal_plus', name: 'Personal Plus' },
    { id: 'personal_premium', name: 'Personal Premium' },
    { id: 'personal_gold', name: 'Personal Gold' },
    { id: 'personal_platinum', name: 'Personal Platinum' },
  ];

  const fetchGym = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/gyms/mine`);
      setGym(res.data);
      setForm({
        name: res.data.name || '',
        cep: res.data.cep || '',
        street: res.data.street || '',
        number: res.data.number || '',
        neighborhood: res.data.neighborhood || '',
        city: res.data.city || '',
        state: res.data.state || '',
        address: res.data.address || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        opening_hours: res.data.opening_hours || '',
        modalities: res.data.modalities || [],
        accepted_plans: res.data.accepted_plans || [],
        plan_modalities: res.data.plan_modalities || {}
      });
      if (res.data.photo_path) {
        loadPhoto(res.data.gym_id);
      }
    } catch {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGym();
  }, [fetchGym]);

  const loadPhoto = async (gymId) => {
    try {
      const res = await axios.get(`${API}/gyms/photo/${gymId}`, { responseType: 'blob' });
      setPhotoUrl(URL.createObjectURL(res.data));
    } catch (e) { console.error('Failed to load gym photo:', e); }
  };

  const [cepLoading, setCepLoading] = useState(false);
  const handleCepLookup = async (cep) => {
    const clean = cep.replace(/\D/g, '');
    setForm(f => ({ ...f, cep }));
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await axios.get(`${API}/cep/${clean}`);
      setForm(f => ({
        ...f,
        street: res.data.street || f.street,
        neighborhood: res.data.neighborhood || f.neighborhood,
        city: res.data.city || f.city,
        state: res.data.state || f.state,
        address: `${res.data.street || ''}${res.data.street && res.data.neighborhood ? ', ' : ''}${res.data.neighborhood || ''}, ${res.data.city || ''}, ${res.data.state || ''}`.replace(/(^,\s*|,\s*$)/g, ''),
        latitude: res.data.latitude || f.latitude,
        longitude: res.data.longitude || f.longitude,
      }));
      toast.success('CEP encontrado!');
    } catch {
      toast.error('CEP não encontrado');
    } finally {
      setCepLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/gyms/mine/profile`, form);
      toast.success('Perfil atualizado!');
      fetchGym();
    } catch {
      toast.error('Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem deve ter no máximo 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/gyms/mine/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Foto atualizada!');
      fetchGym();
    } catch {
      toast.error('Erro ao enviar foto');
    } finally {
      setUploading(false);
    }
  };

  const toggleModality = (mod) => {
    const current = form.modalities || [];
    if (current.includes(mod)) {
      setForm({ ...form, modalities: current.filter(m => m !== mod) });
    } else {
      setForm({ ...form, modalities: [...current, mod] });
    }
  };

  const togglePlan = (plan) => {
    const current = form.accepted_plans || [];
    if (current.includes(plan)) {
      setForm({ ...form, accepted_plans: current.filter(p => p !== plan) });
    } else {
      setForm({ ...form, accepted_plans: [...current, plan] });
    }
  };

  const setPlanModality = (plan, count) => {
    setForm({ ...form, plan_modalities: { ...form.plan_modalities, [plan]: parseInt(count) || 1 } });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="gym-profile">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Editar Perfil</h1>
        <p className="text-slate-600 mt-1">Atualize as informações da sua academia</p>
      </div>

      {/* Photo */}
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Foto da Academia</p>
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center border-2 border-dashed border-slate-300">
              {photoUrl ? (
                <img src={photoUrl} alt="Academia" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-8 h-8 text-slate-400" />
              )}
            </div>
            <div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload-input" />
                <Button variant="outline" size="sm" disabled={uploading} asChild>
                  <span>{uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Enviando...</> : <><Camera className="w-4 h-4 mr-2" /> Alterar Foto</>}</span>
                </Button>
              </label>
              <p className="text-xs text-slate-400 mt-2">Máximo 5MB. JPG, PNG ou WebP</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Info */}
      <Card className="border-slate-100">
        <CardContent className="p-6 space-y-4">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Informações</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="gym-name-input" />
            </div>
            <div>
              <Label className="text-sm">CEP</Label>
              <div className="relative">
                <Input
                  value={form.cep || ''}
                  onChange={(e) => handleCepLookup(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  data-testid="gym-cep-input"
                />
                {cepLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
              </div>
            </div>
            <div>
              <Label className="text-sm">Rua</Label>
              <Input value={form.street || ''} onChange={(e) => setForm({ ...form, street: e.target.value })} data-testid="gym-street-input" />
            </div>
            <div>
              <Label className="text-sm">Número</Label>
              <Input value={form.number || ''} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="Nº" data-testid="gym-number-input" />
            </div>
            <div>
              <Label className="text-sm">Bairro</Label>
              <Input value={form.neighborhood || ''} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} data-testid="gym-neighborhood-input" />
            </div>
            <div>
              <Label className="text-sm">Cidade / UF</Label>
              <div className="flex gap-2">
                <Input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} className="flex-1" data-testid="gym-city-input" />
                <Input value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-16" maxLength={2} data-testid="gym-state-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="gym-phone-input" />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="gym-email-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">Horário de Funcionamento</Label>
              <Input value={form.opening_hours} onChange={(e) => setForm({ ...form, opening_hours: e.target.value })} placeholder="Ex: Seg-Sex 06:00-22:00 | Sáb 08:00-14:00" data-testid="gym-hours-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modalities */}
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Modalidades Oferecidas</p>
          <div className="flex flex-wrap gap-2">
            {MODALITIES.map((mod) => (
              <Button
                key={mod}
                variant={form.modalities?.includes(mod) ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleModality(mod)}
                className={form.modalities?.includes(mod) ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
                data-testid={`modality-${mod}`}
              >
                {form.modalities?.includes(mod) && <Check className="w-3 h-3 mr-1" />}
                {mod}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Plans & Modalities per Plan */}
      <Card className="border-slate-100">
        <CardContent className="p-6">
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Planos Aceitos & Modalidades por Plano</p>
          <div className="space-y-3">
            {PLANS.map((plan) => (
              <div key={plan.id} className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                <Button
                  variant={form.accepted_plans?.includes(plan.id) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => togglePlan(plan.id)}
                  className={form.accepted_plans?.includes(plan.id) ? 'bg-orange-500 hover:bg-orange-600 text-white min-w-[100px]' : 'min-w-[100px]'}
                  data-testid={`plan-toggle-${plan.id}`}
                >
                  {form.accepted_plans?.includes(plan.id) && <Check className="w-3 h-3 mr-1" />}
                  {plan.name}
                </Button>
                {form.accepted_plans?.includes(plan.id) && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-600 whitespace-nowrap">Modalidades:</Label>
                    <Select
                      value={String(form.plan_modalities?.[plan.id] || 1)}
                      onValueChange={(val) => setPlanModality(plan.id, val)}
                    >
                      <SelectTrigger className="w-20" data-testid={`plan-mod-count-${plan.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white w-full" data-testid="save-profile-btn">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Salvando...</> : 'Salvar Perfil'}
      </Button>
    </div>
  );
};

// Gym Students Management (updated to show details + registration request) is at top

// Evaluations Management
const EvaluationsManagement = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewEvalsDialog, setShowViewEvalsDialog] = useState(false);
  const [viewEvalsData, setViewEvalsData] = useState([]);
  const [viewEvalsLoading, setViewEvalsLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    weight: '', height: '', body_fat_percentage: '', lean_mass: '',
    chest: '', waist: '', hip: '', abdomen: '', right_arm: '', left_arm: '',
    right_thigh: '', left_thigh: '', cardio_test_result: '', strength_test_result: '', evaluator_name: ''
  });

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/students`);
      setStudents(res.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleOpenEvaluation = (student) => {
    setSelectedStudent(student);
    setFormData({
      weight: '', height: '', body_fat_percentage: '', lean_mass: '',
      chest: '', waist: '', hip: '', abdomen: '', right_arm: '', left_arm: '',
      right_thigh: '', left_thigh: '', cardio_test_result: '', strength_test_result: '', evaluator_name: ''
    });
    setShowDialog(true);
  };

  const handleViewEvaluations = async (student) => {
    setSelectedStudent(student);
    setViewEvalsLoading(true);
    setShowViewEvalsDialog(true);
    try {
      const res = await axios.get(`${API}/evaluations/student/${student.student_id}?limit=3`);
      setViewEvalsData(res.data);
    } catch {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setViewEvalsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/evaluations`, {
        student_id: selectedStudent.student_id,
        weight: parseFloat(formData.weight),
        height: parseFloat(formData.height),
        body_fat_percentage: parseFloat(formData.body_fat_percentage),
        lean_mass: parseFloat(formData.lean_mass),
        chest: formData.chest ? parseFloat(formData.chest) : null,
        waist: formData.waist ? parseFloat(formData.waist) : null,
        hip: formData.hip ? parseFloat(formData.hip) : null,
        abdomen: formData.abdomen ? parseFloat(formData.abdomen) : null,
        right_arm: formData.right_arm ? parseFloat(formData.right_arm) : null,
        left_arm: formData.left_arm ? parseFloat(formData.left_arm) : null,
        right_thigh: formData.right_thigh ? parseFloat(formData.right_thigh) : null,
        left_thigh: formData.left_thigh ? parseFloat(formData.left_thigh) : null,
        cardio_test_result: formData.cardio_test_result || null,
        strength_test_result: formData.strength_test_result || null,
        evaluator_name: formData.evaluator_name || null
      });
      toast.success('Avaliação registrada com sucesso!');
      setShowDialog(false);
    } catch (error) {
      toast.error('Erro ao registrar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate preview values
  const previewIMC = formData.weight && formData.height
    ? (parseFloat(formData.weight) / ((parseFloat(formData.height) / 100) ** 2)).toFixed(1)
    : null;
  const previewWHR = formData.waist && formData.hip && parseFloat(formData.hip) > 0
    ? (parseFloat(formData.waist) / parseFloat(formData.hip)).toFixed(3)
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Avaliações Físicas
        </h1>
        <p className="text-slate-600 mt-1">Registre avaliações trimestrais dos alunos</p>
      </div>

      {students.length === 0 ? (
        <Card className="border-slate-100">
          <CardContent className="p-12 text-center">
            <ClipboardCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nenhum aluno para avaliar</h3>
            <p className="text-slate-500">Cadastre alunos primeiro para registrar avaliações.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {students.map((student) => (
            <Card key={student.student_id} className="border-slate-100 card-hover">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{student.name}</h3>
                    <p className="text-sm text-slate-500">{student.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleViewEvaluations(student)}
                      data-testid={`view-evals-${student.student_id}`}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Avaliações
                    </Button>
                    <Button
                      onClick={() => handleOpenEvaluation(student)}
                      className="bg-orange-500 hover:bg-orange-600"
                      data-testid={`evaluate-${student.student_id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nova Avaliação
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Evaluation Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Avaliação Física - {selectedStudent?.name}
            </DialogTitle>
            <DialogDescription>Registre os dados da avaliação trimestral</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            {/* IMC/WHR Preview */}
            {(previewIMC || previewWHR) && (
              <div className="bg-orange-50 rounded-xl p-4 grid grid-cols-2 gap-4">
                {previewIMC && <div className="text-center">
                  <p className="text-xs text-orange-600 font-medium">IMC (calculado)</p>
                  <p className="text-2xl font-bold text-orange-700">{previewIMC}</p>
                </div>}
                {previewWHR && <div className="text-center">
                  <p className="text-xs text-orange-600 font-medium">Relação Cintura/Quadril</p>
                  <p className="text-2xl font-bold text-orange-700">{previewWHR}</p>
                </div>}
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Medidas Básicas</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'weight', label: 'Peso (kg) *', ph: '70.5', req: true },
                  { key: 'height', label: 'Altura (cm) *', ph: '175', req: true },
                  { key: 'body_fat_percentage', label: 'Gordura Corporal (%) *', ph: '18.5', req: true },
                  { key: 'lean_mass', label: 'Massa Magra (kg) *', ph: '55', req: true }
                ].map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={`gym-${f.key}`}>{f.label}</Label>
                    <Input id={`gym-${f.key}`} type="number" step="0.1" value={formData[f.key]}
                      onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} placeholder={f.ph} required={f.req} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Circunferências (cm)</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'chest', label: 'Tórax', ph: '95' },
                  { key: 'waist', label: 'Cintura', ph: '80' },
                  { key: 'hip', label: 'Quadril', ph: '95' },
                  { key: 'abdomen', label: 'Abdômen', ph: '85' },
                  { key: 'right_arm', label: 'Braço D', ph: '32' },
                  { key: 'left_arm', label: 'Braço E', ph: '32' },
                  { key: 'right_thigh', label: 'Coxa D', ph: '55' },
                  { key: 'left_thigh', label: 'Coxa E', ph: '55' }
                ].map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={`gym-${f.key}`}>{f.label}</Label>
                    <Input id={`gym-${f.key}`} type="number" step="0.1" value={formData[f.key]}
                      onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })} placeholder={f.ph} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Testes</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Teste Cardiorrespiratório</Label>
                  <Input value={formData.cardio_test_result}
                    onChange={(e) => setFormData({ ...formData, cardio_test_result: e.target.value })} placeholder="Bom, Regular, Excelente" />
                </div>
                <div className="space-y-1">
                  <Label>Teste de Força</Label>
                  <Input value={formData.strength_test_result}
                    onChange={(e) => setFormData({ ...formData, strength_test_result: e.target.value })} placeholder="Bom, Regular, Excelente" />
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Nome do Avaliador</Label>
              <Input value={formData.evaluator_name}
                onChange={(e) => setFormData({ ...formData, evaluator_name: e.target.value })} placeholder="Prof. Carlos" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancelar</Button>
              <Button type="submit" data-testid="submit-evaluation-btn"
                disabled={submitting || !formData.weight || !formData.height || !formData.body_fat_percentage || !formData.lean_mass}
                className="flex-1 bg-orange-500 hover:bg-orange-600">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Avaliação'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Evaluations Dialog */}
      <Dialog open={showViewEvalsDialog} onOpenChange={setShowViewEvalsDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Eye className="w-5 h-5 text-orange-500" />
              Últimas Avaliações - {selectedStudent?.name}
            </DialogTitle>
            <DialogDescription>Resultados das 3 últimas avaliações</DialogDescription>
          </DialogHeader>
          {viewEvalsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : viewEvalsData.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhuma avaliação registrada</p>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {viewEvalsData.map((ev, idx) => (
                <div key={ev.evaluation_id || idx} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-900">
                      Avaliação {idx + 1} — {ev.created_at ? new Date(ev.created_at).toLocaleDateString('pt-BR') : '-'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      ev.evolution_classification === 'high' ? 'bg-emerald-100 text-emerald-700' :
                      ev.evolution_classification === 'moderate' ? 'bg-blue-100 text-blue-700' :
                      ev.evolution_classification === 'regression' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {ev.evolution_classification === 'high' ? 'Alta evolução' :
                       ev.evolution_classification === 'moderate' ? 'Moderada' :
                       ev.evolution_classification === 'regression' ? 'Regressão' : 'Estável'}
                      {ev.evolution_percentage ? ` (${ev.evolution_percentage > 0 ? '+' : ''}${ev.evolution_percentage.toFixed(1)}%)` : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-slate-500">Peso:</span> <strong>{ev.weight} kg</strong></div>
                    <div><span className="text-slate-500">Altura:</span> <strong>{ev.height} cm</strong></div>
                    <div><span className="text-slate-500">IMC:</span> <strong>{ev.imc?.toFixed(1)}</strong></div>
                    <div><span className="text-slate-500">Gordura:</span> <strong>{ev.body_fat_percentage}%</strong></div>
                    <div><span className="text-slate-500">Massa Magra:</span> <strong>{ev.lean_mass} kg</strong></div>
                    {ev.waist_hip_ratio && <div><span className="text-slate-500">Cintura/Quadril:</span> <strong>{ev.waist_hip_ratio}</strong></div>}
                    {ev.chest && <div><span className="text-slate-500">Tórax:</span> <strong>{ev.chest} cm</strong></div>}
                    {ev.waist && <div><span className="text-slate-500">Cintura:</span> <strong>{ev.waist} cm</strong></div>}
                    {ev.hip && <div><span className="text-slate-500">Quadril:</span> <strong>{ev.hip} cm</strong></div>}
                    {ev.abdomen && <div><span className="text-slate-500">Abdômen:</span> <strong>{ev.abdomen} cm</strong></div>}
                    {ev.right_arm && <div><span className="text-slate-500">Braço D:</span> <strong>{ev.right_arm} cm</strong></div>}
                    {ev.left_arm && <div><span className="text-slate-500">Braço E:</span> <strong>{ev.left_arm} cm</strong></div>}
                    {ev.right_thigh && <div><span className="text-slate-500">Coxa D:</span> <strong>{ev.right_thigh} cm</strong></div>}
                    {ev.left_thigh && <div><span className="text-slate-500">Coxa E:</span> <strong>{ev.left_thigh} cm</strong></div>}
                  </div>
                  {(ev.cardio_test_result || ev.strength_test_result || ev.evaluator_name) && (
                    <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-3 gap-3 text-sm">
                      {ev.cardio_test_result && <div><span className="text-slate-500">Cardio:</span> <strong>{ev.cardio_test_result}</strong></div>}
                      {ev.strength_test_result && <div><span className="text-slate-500">Força:</span> <strong>{ev.strength_test_result}</strong></div>}
                      {ev.evaluator_name && <div><span className="text-slate-500">Avaliador:</span> <strong>{ev.evaluator_name}</strong></div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Main Gym Dashboard Component
const GymDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { path: '/gym', label: t('gymNav.dashboard'), icon: <Home className="w-5 h-5" /> },
    { path: '/gym/students', label: t('gymNav.students'), icon: <Users className="w-5 h-5" /> },
    { path: '/gym/checkins', label: t('gymNav.checkins'), icon: <Calendar className="w-5 h-5" /> },
    { path: '/gym/history', label: t('gymNav.history'), icon: <History className="w-5 h-5" /> },
    { path: '/gym/evaluations', label: t('gymNav.evaluations'), icon: <ClipboardCheck className="w-5 h-5" /> },
    { path: '/gym/profile', label: t('gymNav.profile'), icon: <Building2 className="w-5 h-5" /> },
    { path: '/gym/financial', label: t('gymNav.financial'), icon: <DollarSign className="w-5 h-5" /> },
    { path: '/gym/catraca', label: t('gymNav.catraca'), icon: <Code className="w-5 h-5" /> },
    { path: '/gym/contact', label: t('gymNav.contact'), icon: <Mail className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" />
      
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-40 hidden lg:block safe-top">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <Activity className="w-8 h-8 text-orange-500" />
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Vital
            </span>
          </div>
          
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/gym'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-orange-500 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <span className="text-orange-600 font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{t('gym.roleLabel')}</p>
            </div>
          </div>
          <div className="mb-3">
            <LanguageSwitcher />
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('common.logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass fixed top-0 w-full z-50 border-b border-slate-200/50 safe-top">
        <div className="px-4 h-14 flex items-center justify-between safe-x">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-orange-500" />
            <span className="font-bold text-slate-900">Vital</span>
          </div>
          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <span className="text-orange-600 font-semibold text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pb-20 lg:pb-0" style={{ paddingTop: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}>
        <div className="p-4 sm:p-6 lg:p-8 lg:pt-8" style={{ paddingTop: undefined }}>
          <div className="lg:pt-0">
          <Routes>
            <Route index element={<GymOverview />} />
            <Route path="students" element={<StudentsManagement />} />
            <Route path="checkins" element={<CheckinsManagement />} />
            <Route path="history" element={<CheckinHistory />} />
            <Route path="evaluations" element={<EvaluationsManagement />} />
            <Route path="profile" element={<GymProfile />} />
            <Route path="financial" element={<GymFinancial />} />
            <Route path="catraca" element={<CatracaAPIPage />} />
            <Route path="contact" element={<GymContactPage />} />
          </Routes>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 safe-bottom safe-x">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/gym'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-1 py-2 min-w-0 ${
                  isActive ? 'text-orange-500' : 'text-slate-500'
                }`
              }
            >
              {item.icon}
              <span className="text-[10px] leading-tight truncate max-w-[50px] text-center">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default GymDashboard;
